import { PopperProps } from '@mui/material/Popper'
import { styleConstants } from '@paywithextend/components'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import DynamicsBCIcon from 'ext-ui/svg/icon/DynamicsBCIcon'
import SageIntacctIcon from 'ext-ui/svg/icon/SageIntacctIcon'
import XeroIcon from 'ext-ui/svg/icon/XeroIcon'
import React from 'react'
import QuickBooks from 'src/assets/images/QuickBooks.svg'
import SvgNetSuiteIcon from 'src/components/Svgs/SvgNetSuiteIcon'
import SvgTransactionPushFailed from 'src/components/Svgs/SvgTransactionPushFailed'
import SvgTransactionPushPending from 'src/components/Svgs/SvgTransactionPushPending'
import { OrganizationConnectedPlatform } from 'src/enums'
import Trns from 'src/localization/Trns'
import { ConnectedPlatformStatus } from 'src/models/transaction.models'
import { css } from 'styled-components'

interface TransactionPushStatusTooltipProps {
  className?: string
  placement?: PopperProps['placement']
  status: ConnectedPlatformStatus | undefined
  platform: OrganizationConnectedPlatform | undefined
  connectedPlatformName: string
}

const TransactionPushStatusTooltip: React.FC<
  TransactionPushStatusTooltipProps
> = (props): React.ReactElement => {
  const erpSvgIconCss = css`
    width: 24px;
    height: 24px;
  `

  const iconForStatus = (
    status: ConnectedPlatformStatus | undefined,
  ): React.ReactElement | undefined => {
    switch (status) {
      case ConnectedPlatformStatus.NotSynced: {
        return undefined
      }
      case ConnectedPlatformStatus.Pending: {
        return <SvgTransactionPushPending data-testid={'erpSyncInProgress'} />
      }
      case ConnectedPlatformStatus.Failed: {
        return <SvgTransactionPushFailed data-testid={'erpSyncFailed'} />
      }
      case ConnectedPlatformStatus.Success: {
        switch (props.platform) {
          case OrganizationConnectedPlatform.NETSUITE: {
            return <SvgNetSuiteIcon data-testid={'erpSyncSuccess'} />
          }
          case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX:
          case OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP:
          case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE: {
            return (
              <img
                src={QuickBooks}
                css={css`
                  transform: scale(0.6);
                `}
                alt={''}
                data-testid={'erpSyncSuccess'}
              />
            )
          }
          case OrganizationConnectedPlatform.XERO:
            return (
              <XeroIcon css={erpSvgIconCss} data-testid={'erpSyncSuccess'} />
            )
          case OrganizationConnectedPlatform.SAGE_INTACCT: {
            return (
              <SageIntacctIcon
                css={erpSvgIconCss}
                data-testid={'erpSyncSuccess'}
              />
            )
          }
          case OrganizationConnectedPlatform.DYNAMICS_BC: {
            return (
              <DynamicsBCIcon
                css={erpSvgIconCss}
                width={24}
                height={24}
                data-testid={'erpSyncSuccess'}
              />
            )
          }
        }
      }
    }
  }

  const tooltipForStatus = (
    status: ConnectedPlatformStatus | undefined,
  ): React.ReactElement | undefined => {
    switch (status) {
      case ConnectedPlatformStatus.NotSynced: {
        return undefined
      }
      case ConnectedPlatformStatus.Pending: {
        return <Trns source={'Sync in progress'} />
      }
      case ConnectedPlatformStatus.Failed: {
        return <Trns source={'Sync failed'} />
      }
      case ConnectedPlatformStatus.Success: {
        return (
          <Trns
            source={'Connected to ${connection}'}
            expressions={{
              connection: (
                <span
                  css={css`
                    font-family: ${styleConstants.fontFamilies.medium};
                  `}
                >
                  {props.connectedPlatformName}
                </span>
              ),
            }}
          />
        )
      }
    }
  }

  return (
    <TooltipIcon
      className={props.className}
      placement={props.placement || 'right'}
      icon={iconForStatus(props.status)}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          height: 16px;
        `}
      >
        <p>{tooltipForStatus(props.status)}</p>
      </div>
    </TooltipIcon>
  )
}

export default TransactionPushStatusTooltip
